import '@albelli/arc-3/lib/price.css';
import '@albelli/arc-3/lib/forms.css';
import '@albelli/arc-3/lib/buttons.css';
import '@albelli/arc-3/lib/callout_label.css';
import '../../style/main.scss';
import '../../style/offer-block.scss';

import useVendor from '../../hooks/useVendor';

import CopyToClipboard from './copy-to-clipboard';
import EditorButton from './editor-button';
import Trans from '../translations/trans';
import DateFormat from '../date/dateFormat';
import Price from './price';
import { domainPrefix } from '../../config/domainPrefix';
import { getPromoLabelPrefix, getPrices, getUspMarkup } from '../../helpers/offerBlockHelper';
import currencyFormatter from '../../helpers/currencyFormatter';
import { editorConfigMobile } from '../../config/editorConfig';

type OfferblockProps = {
    promotion: any;
    promoCode: string;
    showPricing: boolean;
    showUsp: boolean;
    productPrices: any;
    category: string;
    campaignUuid: string;
    fastLoad: boolean;
    key?: number | string;
};

const Offerblock = ({
    promotion,
    promoCode,
    showPricing,
    showUsp,
    productPrices,
    category,
    campaignUuid,
    fastLoad,
}: OfferblockProps) => {
    const promoLabel = promotion.highestOffer ? promotion.highestOffer : '';
    const promoLabelPrefix = getPromoLabelPrefix(promotion.offers, promotion.highestOffer);
    const vendor = useVendor();
    const articleTypes = Object.keys(promotion.offers).join('-').toLowerCase();

    // currently the campaign manager gives us a s3 URL, if this is the case we rewrite this to the CDN URL
    // so we can use the resizer Lambda to get properly sized images for our SRCsets
    // This processing can be removed when the API is adapted to serve the CDN URL
    let promoImage = '';
    const config = editorConfigMobile[Object.keys(promotion.offers)[0]] ?? [];
    if (config.length == 0) {
        console.error(`editorConfigMobile not setup for ${articleTypes}`);
        return;
    }
    if (promotion.image.includes('amazonaws')) {
        const regEx = /\/content\/.*/;
        const image = regEx.exec(promotion.image);
        const imgUrl = image && image[0];
        promoImage = `https://${domainPrefix}${vendor + imgUrl}`;
    } else {
        promoImage = promotion.image;
    }

    const getPromoLabel = () =>
        promotion.promoLabel ? (
            promotion.promoLabel
        ) : (
            <>
                {promoLabelPrefix ? <Trans transKey={promoLabelPrefix} /> : ''} -
                {promotion.offerType === 'percentage' ? `${promoLabel}%` : currencyFormatter(promoLabel, vendor)}
            </>
        );

    const getPromoLabelBlock = () => {
        const showPromoLabel =
            (promotion.promoLabel || promoLabel) &&
            (promotion.offerType === 'percentage' || promotion.offerType === 'fixed_amount');

        return showPromoLabel ? (
            <div className={`arc3-callout-label --category-${category}`}>
                <p className='arc3-typo--text-uppercase arc3-typo--font-bold'>{getPromoLabel()}</p>
            </div>
        ) : (
            ''
        );
    };

    return (
        <div className='offer-block' data-articleTypes={articleTypes}>
            {getPromoLabelBlock()}
            <div className='offer-block__wrapper --top arc3-flex-wrapper'>
                <a href={promotion.url}>
                    <picture>
                        <source srcSet={`${promoImage}?d=385x308`} media='(min-width:780px)' />
                        <source srcSet={`${promoImage}?d=559x447`} media='(min-width: 575px)' />
                        <img
                            loading={fastLoad ? 'eager' : 'lazy'}
                            src={`${promoImage}?d=310x248`}
                            width='310'
                            height='248'
                        />
                    </picture>
                </a>
                <div className='offer-block__title-wrapper arc3-margin--left'>
                    <p className='offer-block__title arc3-typo--font-bold arc3-margin--top arc3-no-margin--bottom'>
                        {promotion.title}
                    </p>
                    <small className='arc3-margin--bottom'>
                        {/* @TODO take offer extensions into account */}
                        <DateFormat
                            isoDateFormattedString={promotion.endDate}
                            dateFormatTransKey='promoBar.endDateFormat'
                        />
                    </small>
                </div>
            </div>
            {showUsp ? getUspMarkup(promotion) : ''}

            <div className='arc3-flex-wrapper --flex-direction-row --align-items-baseline arc3-margin--left'>
                {showPricing ? <Price prices={getPrices(promotion.offers)} /> : ''}
                <div
                    className={`arc3-hide-for-large flex-grow-1 ${
                        showPricing ? 'arc3-margin--left-lg' : ''
                    } arc3-margin--right`}
                >
                    <p className='arc3-no-margin--bottom'>
                        <Trans transKey='discountOverview.discountCode.title' />
                    </p>

                    <CopyToClipboard promocode={promoCode} />
                </div>
            </div>

            <p className='arc3-hide-for-medium-down arc3-margin--left arc3-no-margin--bottom'>
                <Trans transKey='discountOverview.discountCode.title' />
            </p>
            <div className='offer-block__wrapper --bottom arc3-padding'>
                <div className='arc3-hide-for-medium-down'>
                    <CopyToClipboard promocode={promoCode} />
                </div>
                <EditorButton
                    promotionUrl={promotion.url}
                    articleTypes={promotion.offers}
                    campaignUuid={campaignUuid}
                />
            </div>
        </div>
    );
};

export default Offerblock;
